/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useEffect, useState } from 'react';
import { StyleSheet, css } from 'aphrodite';
import { mixins, colors } from '@blend/consumer-components';

import { useIsTrackEnabled } from '../../../../hooks/videoProvider/useIsTrackEnabled';
import { useMediaStreamTrack } from '../../../../hooks/videoProvider/useMediaStreamTrack';

export namespace VolumeMeter {
  export interface Props extends React.HTMLAttributes<HTMLDivElement> {
    audioTrack?: VideoProvider.ILocalAudioTrack;
  }
}

const styles = StyleSheet.create({
  meterWrapper: {
    width: '100%',
    ...mixins.margin(0, 0, 16, 0),
  },
  box: {
    width: 'calc((100% - 10px)/6)',
    height: 12,
    marginRight: 2,
    backgroundColor: colors.catalystLight03,
    borderRadius: 4,
    display: 'inline-block',
    ':last-child': {
      marginRight: 0,
    },
  },
  filledBox: {
    backgroundColor: colors.reflex,
  },
});

const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
let audioContext: AudioContext;

export function initializeAnalyser(stream: MediaStream) {
  audioContext = audioContext || new AudioContext();
  const audioSource = audioContext.createMediaStreamSource(stream);

  const analyser = audioContext.createAnalyser();
  analyser.smoothingTimeConstant = 0.2;
  analyser.fftSize = 256;

  audioSource.connect(analyser);
  return analyser;
}

function createNormalizedMediaStream(trackOrStream: MediaStream | MediaStreamTrack) {
  if (trackOrStream instanceof MediaStreamTrack) {
    // If the input is a MediaStreamTrack, create a new MediaStream containing this track
    return new MediaStream([trackOrStream.clone()]);
  } else {
    // If the input is already a MediaStream, clone it to ensure we're not mutating the original
    return new MediaStream(trackOrStream.clone());
  }
}

export const VolumeMeter: React.FC<VolumeMeter.Props> = ({ audioTrack, ...htmlProps }) => {
  const [analyser, setAnalyser] = useState<AnalyserNode>();
  const [volume, setVolume] = useState(0);
  const isTrackEnabled = useIsTrackEnabled(audioTrack);
  const { mediaStreamOrTrack } = useMediaStreamTrack(audioTrack);

  useEffect(() => {
    if (audioTrack && mediaStreamOrTrack && isTrackEnabled) {
      // Here we create a new MediaStream from a clone of the mediaStreamTrack.
      // A clone is created to allow multiple instances of this component for a single
      // AudioTrack on iOS Safari.
      let newMediaStream = createNormalizedMediaStream(mediaStreamOrTrack);

      // Here we listen for the 'stopped' event on the audioTrack. When the audioTrack is stopped,
      // we stop the cloned track that is stored in 'newMediaStream'. It is important that we stop
      // all tracks when they are not in use. Browsers like Firefox don't let you create a new stream
      // from a new audio device while the active audio device still has active tracks.
      const stopAllMediaStreamTracks = () => newMediaStream.getTracks().forEach(track => track.stop());
      // TODO: (ZOOMSDK) - check if support for 'stopped' needs to be added
      audioTrack.on && audioTrack.on('stopped', stopAllMediaStreamTracks);

      const reinitializeAnalyser = () => {
        stopAllMediaStreamTracks();
        newMediaStream = createNormalizedMediaStream(mediaStreamOrTrack);
        setAnalyser(initializeAnalyser(newMediaStream));
      };

      setAnalyser(initializeAnalyser(newMediaStream));

      // Here we reinitialize the AnalyserNode on focus to avoid an issue in Safari
      // where the analysers stop functioning when the user switches to a new tab
      // and switches back to the app.
      window.addEventListener('focus', reinitializeAnalyser);

      return () => {
        stopAllMediaStreamTracks();
        window.removeEventListener('focus', reinitializeAnalyser);
        audioTrack.off && audioTrack.off('stopped', stopAllMediaStreamTracks);
      };
    }

    return;
  }, [isTrackEnabled, mediaStreamOrTrack, audioTrack]);

  useEffect(() => {
    if (isTrackEnabled && analyser) {
      const frequencyArray = new Uint8Array(analyser.frequencyBinCount);

      const timer = setInterval(() => {
        analyser.getByteFrequencyData(frequencyArray);
        const frequencyArraySum = frequencyArray.reduce((curr, sum) => curr + sum, 0);
        const averageFrequency = frequencyArraySum / frequencyArray.length;
        const normalizedVolume = Math.ceil(Math.min(6, Math.max(0, Math.log10(averageFrequency / 3) * 3)));
        setVolume(normalizedVolume);
      }, 100);

      return () => {
        clearInterval(timer);
      };
    }
    return;
  }, [isTrackEnabled, analyser]);

  const boxes = [];
  for (let i = 0; i <= 5; i++) {
    boxes.push(<span key={i} className={css(styles.box, i < volume && styles.filledBox)} />);
  }

  return (
    <div className={css(styles.meterWrapper)} {...htmlProps}>
      {boxes}
    </div>
  );
};
